import { tap } from 'rxjs/operators'
import * as THREE from 'three'

import { Component } from '@angular/core'
import { Feature } from '@classes/Feature'
import { Scene } from '@classes/Scene'
import { reposition } from '@classes/Transitions'
import { DeleteComponent } from '@modal/delete/delete.component'
import { TypeToConfirmComponent } from '@modal/type-to-confirm/type-to-confirm.component'
import { ContextMenuService } from '@services/context-menu.service'
import { CopyService } from '@services/copy.service'
import { EnvironmentManagerService } from '@services/environment-manager.service'
import { FeatureService } from '@services/feature.service'
import { LoadingService } from '@services/loading.service'
import { ModalService } from '@services/modal.service'
import { SceneService } from '@services/scene.service'
import { UndoService } from '@services/undo.service'
import { VirtualTourService } from '@services/virtual-tour.service'
import { ToastService } from '@services/toast.service'

@Component({
  selector: 'shared-leftside-menu',
  templateUrl: './leftside-menu.component.html',
  styleUrls: ['./leftside-menu.component.css'],
  standalone: false
})
export class LeftSideMenuComponent {
  get clickInfo() { return this.contextMenuService.clickInfo }
  get isMapScene() { return this._sceneService.selectedScene.type == 'Map' }
  get sceneNotProcessing() {
    if (this.clickInfo?.type == "scene") {
      const scene = this.clickInfo?.data as Scene

      return scene?.state != "processing" && scene?.state != "failed"
    }

    return true
  }

  constructor(
    private _copyService: CopyService,
    private _featureService: FeatureService,
    private _loadingService: LoadingService,
    private _modalService: ModalService,
    private _sceneService: SceneService,
    private _undoService: UndoService,
    private _virtualTourService: VirtualTourService,
    public contextMenuService: ContextMenuService,
    public envManager: EnvironmentManagerService,
    private _toastService: ToastService
  ) { }

  copy() {
    if (this.clickInfo?.type == 'feature') {
      const feature = this.clickInfo.data as Feature

      this._copyService.copyFeatureToClipboard(feature)
    } else if (this.clickInfo?.type == 'scene') {
      const scene = this.clickInfo.data as Scene

      this._copyService.copySceneToClipboard(scene)
    }

    this.contextMenuService.closeContext()
  }

  delete() {
    const type = this.clickInfo?.type

    if (type == 'feature') this._deleteFeature()
    else if (type == 'scene') this._deleteScene()

    this.contextMenuService.closeContext()
  }

  _deleteFeature() {
    const feature = this.clickInfo.data as Feature

    const name = feature.name
    const onSubmit = () => {
      this.envManager.sceneManager.removeFeatureFromScene(feature)
      
      // Show loading toast for groups (which can take longer to delete)
      let loadingToast = null
      if (feature.type === 'group') {
        loadingToast = this._toastService.toast({
          title: `Deleting ${name}`,
          message: 'This may take a few seconds for large groups...',
          color: 'blue',
          autohide: false
        })
      }
      
      this._featureService.deleteFeature(feature).pipe(
        // TODO: Don't clear when undoing deleteFeature is allowed
        tap(() => this._undoService.clear.bind(this._undoService)())
      ).subscribe({
        next: () => {
          // Hide loading toast if it exists
          if (loadingToast) {
            loadingToast.toast.hide()
          }
        },
        error: (error) => {
          // Hide loading toast and show error if it exists
          if (loadingToast) {
            loadingToast.toast.hide()
          }
          console.error('Error deleting feature:', error)
        }
      })
    }

    this._showDeleteFeatureModal(name, onSubmit)
  }

  _deleteScene() {
    const scene = this.clickInfo.data as Scene
    const name = scene.name
    const onSubmit = () => this._sceneService.deleteScene(scene).subscribe()

    this._showDeleteSceneModal(name, onSubmit)
  }

  _showDeleteFeatureModal(name: string, onSubmit: CallableFunction) {
    const message = `"${name}" and all of its data will be deleted`

    this._modalService.showAsModal<DeleteComponent>(DeleteComponent).then(componentRef => {
      componentRef.instance.title = `Delete ${name}`
      componentRef.instance.message = message
      componentRef.instance.submitText = 'Delete'
      componentRef.instance.submitColor = 'red'
      componentRef.instance.onSubmit = onSubmit.bind(this)
    })
  }

  _showDeleteSceneModal(name: string, onSubmit: CallableFunction) {
    this._modalService.showAsModal<TypeToConfirmComponent>(TypeToConfirmComponent)
      .then((componentRef) => {
        componentRef.instance.title = `Delete ${name}`
        componentRef.instance.message = `"${name}" and all of its data will be deleted. <strong>THIS ACTION CANNOT BE UNDONE.</strong>`
        componentRef.instance.submitText = 'Delete'
        componentRef.instance.submitColor = 'red'
        componentRef.instance.confirmWord = 'DELETE SCENE'
        componentRef.instance.onSubmit = onSubmit.bind(this)
      })
  }

  duplicate() {
    if (this.clickInfo?.type == 'feature') {
      const feature = this.clickInfo.data as Feature

      this._loadingService.await(
        this._copyService.duplicateFeature(feature.id, this._sceneService.selectedScene.id)
      )
    } else if (this.clickInfo?.type == 'scene') {
      const scene = this.clickInfo.data as Scene

      this._copyService.duplicateScene(scene.id, this._sceneService.selectedScene.projectID).subscribe()
    }

    this.contextMenuService.closeContext()
  }

  removeFromGroup() {
    const feature = this._featureService.getFeature(this.clickInfo.data.id)
    const parent = this._featureService.getFeature(feature.parentID)

    this._featureService.removeFeatureFromGroup(feature, { newParentID: parent.parentID }).subscribe()
    this.contextMenuService.closeContext()
  }

  ungroup() {
    const group = this._featureService.getFeature(this.clickInfo.data.id)
    this._featureService.removeFeaturesFromGroup(group).subscribe()
    this.contextMenuService.closeContext()
  }

  focusOn() {
    const feature = this._featureService.getFeature(this.clickInfo.data.id)

    if (this.isMapScene) {
      this.envManager.focusOnFeatures(feature)
    } else {
      if (this._virtualTourService.viewpointMode) {
        this._virtualTourService.exitViewpoint()
      }

      if (feature.type == 'group') {
        const descendants = this._featureService.getDescendants(feature)

        if (descendants.length > 0) {
          this.envManager.focusOnFeatures(...descendants)
        }
      } else {
        const model = this.envManager.sceneManager.getFeatureGroup(feature.id)

        if (model) {
          this.envManager.focusOnModels(model)
        } else {
          const camera = this.envManager.modelSpace.camera
          const controls = this.envManager.modelSpace.orbitControls
          const position = new THREE.Vector3().fromArray(feature.position)

          if (this._sceneService.selectedScene.type == '360 Image') {
            this.envManager.lookAt(camera, controls, position)
          } else {
            reposition(camera, controls, position, true)
          }
        }
      }
    }

    this.contextMenuService.closeContext()
  }
}